import React, { Component } from "react";
import styled from "styled-components";
import { app } from "js/namespaces";
import { FeatureType } from "common/features";
import { getStaticUrl } from "js/config";
import { BlueButton } from "js/react/components/UiComponents";
import { themeColors } from "../sharedStyles";
import { UIController } from "js/editor/dialogs/UIController";
import { getPricingPageUrl } from "js/core/utilities/pricing";
import { ds } from "js/core/models/dataService";

const CenterAlignContainer = styled.div`
  height: calc(100% - 80px);
  display: flex;
  align-items: center;
  grid-column: 1/-1;
`;

const NoticeContainer = styled.div`
  max-width: 700px;
  width: 100%;
  background: white;
  padding: 40px;
  margin: 0 auto;
  text-align: center;

  & > * {
    margin-bottom: 20px !important;
  }

  img {
    max-width: 250px;
  }

  h3 {
    margin: 0;
    font-size: 20px;
    color: ${themeColors.darkGray};
    font-weight: bold;
  }

  p {
    font-size: 14px;
    color: ${themeColors.gray};
  }

  a {
    color: ${themeColors.ui_blue};
    font-weight: bold;
    text-decoration: none;
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }
  }
`;

const ExtraContentContainer = styled.div`
    padding-top: 15px;
`;

export const Notice = props => {
    let {
        image = "shake",
        imageWidth = null,
        title = "This is the title of the notice. It should be replaced.",
        message,
        ctaLabel,
        onCtaClick,
        children,
    } = props;

    if (image === "sad") {
        image = getStaticUrl(`/images/bai-bot/bai-bot-sad.png`);
        imageWidth = 60;
    } else if (image === "shake") {
        image = getStaticUrl(`/images/bai-bot/bai-bot-shake.gif`);
        imageWidth = 60;
    }

    return (
        <CenterAlignContainer>
            <NoticeContainer>
                {
                    image &&
                    <img src={image} width={imageWidth} />
                }
                {
                    title &&
                    <h3>{title}</h3>
                }
                {
                    message &&
                    <p>{message}</p>
                }
                {
                    ctaLabel &&
                    <BlueButton onClick={onCtaClick}>{ctaLabel}</BlueButton>
                }
                {
                    !!children &&
                    <ExtraContentContainer>
                        {children}
                    </ExtraContentContainer>
                }
            </NoticeContainer>
        </CenterAlignContainer>
    );
};

export const NoMatchNotice = props => {
    props = {
        image: "sad",
        title: "No matches found.",
        ...props,
    };

    return <Notice {...props} />;
};

export const NoMatchTemplatesNotice = ({
    onAddClassicSlide,
    urlSuggestionForm,
}) => {
    let ctaLabel = null;
    let onCtaClick = null;
    if (app.user.features.isFeatureEnabled(FeatureType.WORKSPACE_CAN_ACCESS_CLASSIC_SLIDES, app.appState.workspaceId)) {
        ctaLabel = "Add Classic Slide";
        onCtaClick = onAddClassicSlide;
    }

    return (
        <NoMatchNotice
            message="Try a different search term or design your slide from scratch with a blank Classic Slide."
            ctaLabel={ctaLabel}
            onCtaClick={onCtaClick}
        >
            <a
                target="_blank"
                href={urlSuggestionForm}
            >
                Suggest a Smart Slide Template
            </a>
        </NoMatchNotice>
    );
};

// TEAM NOTICES

const openPricingPage = () => {
    const userIsBasicInWorkspace = app.user.features.isFeatureEnabled(FeatureType.UPGRADE, app.appState.workspaceId);
    const currentPlan = userIsBasicInWorkspace ? "basic" : "pro";
    window.open(getPricingPageUrl(app && app.user.hasTakenTrial, app && app.user.has("hasTakenTeamTrial"), currentPlan), "_blank");
};

const openSalesCalendar = () => {
    window.open("https://calendly.com/beautifulteam/demo-call-with-beautiful-ai-upgrade-to-team", "_blank");
};

const NoTeamContentNotice = ({
    image,
    titleLead,
    messageLead,
    titleMember,
    messageMember,
    titleNoTeam,
    messageNoTeam,
}) => {
    let isLibOrOwner = ds.teams.isUserLibrarianOrOwner(app.appState.orgId);

    if (UIController.isTeamWorkspace()) {
        if (isLibOrOwner) {
            return (
                <Notice
                    image={image}
                    title={titleLead}
                    message={messageLead}
                />
            );
        } else {
            return (
                <Notice
                    image={image}
                    title={titleMember}
                    message={messageMember}
                />
            );
        }
    } else {
        return (
            <Notice
                image={image}
                title={titleNoTeam}
                message={messageNoTeam}
                ctaLabel="Talk To An Expert"
                onCtaClick={openSalesCalendar}
            />
        );
    }
};

export const NoTeamTemplateNotice = () => (
    <NoTeamContentNotice
        image={getStaticUrl(`/images/notice-banner/team-templates.gif`)}
        titleLead="Create a Template for your team to start from."
        messageLead={
            <>
                <span>Give your team a headstart, with structure and key slides already in place. </span>
                <br />
                <span>Check out our </span>
                <a
                    target="_blank"
                    href="https://support.beautiful.ai/hc/en-us/articles/4405716068365-Team-Template-Overview"
                >FAQs</a>
                <span> to learn more.</span>
            </>
        }
        titleMember="There are no published templates in this workspace yet."
        messageMember="Team Templates are created by Team Librarians or Owners to give you a headstart, with structure, key slides and design elements already in place."
        titleNoTeam="Share presentation templates with your team."
        messageNoTeam={
            <>
                <span>Give your team a headstart with presentation structure, key slides and design elements already in place. </span>
                <a onClick={openPricingPage}>See our Plans</a>
            </>
        }
    />
);

export const NoTeamSlideNotice = () => (
    <NoTeamContentNotice
        image={getStaticUrl(`/images/notice-banner/team-slides.gif`)}
        titleLead="Create a shared library of finished Team Slides."
        messageLead={
            <>
                <span>Simply create a slide once, tag it, and your team can use it infinitely. Update your slide and it'll update everywhere it's used. Check out our </span>
                <a
                    target="_blank"
                    href="https://support.beautiful.ai/hc/en-us/articles/360047188952-Shared-Slides-Overview"
                >FAQs</a>
                <span> to learn more.</span>
            </>
        }
        titleMember="No slides have been shared with your team yet."
        messageMember="These are the slides you use over and over. Team Librarians and Owners can create a searchable library of finished slides for you to grab and go. When a slide is updated, it'll update everywhere it's used."
        titleNoTeam="Share slides with your team."
        messageNoTeam={
            <>
                <span>These are the slides you use over and over. Create a slide once, share it, and your team can use it infinitely. When a slide is updated, it'll update everywhere it's used. </span>
                <a onClick={openPricingPage}>See our Plans</a>
            </>
        }
    />
);

export const NoTeamAssetNotice = () => (
    <NoTeamContentNotice
        image={getStaticUrl(`/images/notice-banner/team-assets.gif`)}
        titleLead="Keep your team on-track and on-brand with Team Assets."
        messageLead={
            <>
                <span>Create a library of logos and brand assets for your team to add to their slides. </span>
                <br />
                <span>Check out our </span>
                <a
                    target="_blank"
                    href="https://support.beautiful.ai/hc/en-us/articles/5671414223757-Team-Assets-"
                >FAQs</a>
                <span> to learn more.</span>
            </>
        }

        titleMember="No assets have been shared with your team yet."
        messageMember="Stop searching for the right logo or image. Team Librarians and Owners can create a library of brand assets so your presentation is always on brand and up to date."
        titleNoTeam="Share images and logos with your team."
        messageNoTeam={
            <>
                <span>Kepp your team's presentations consistently on brand and up to date with a library of approved brand assets. When an asset is updated, it'll update everywhere it's used. </span>
                <a onClick={openPricingPage}>See our Plans</a>
            </>
        }
    />
);
